import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoUrl from '@/assets/img/Logo Light Mode.png';
import type { Candidato } from '../models';
import { montarCampos, montarCamposEtapa2, montarComSeguranca } from './respostasCandidato';

/**
 * PDF com as perguntas e respostas do formulário do candidato (Etapas 1 e 2) — pedido do RH,
 * que às vezes precisa baixar esses dados. Usa exatamente as mesmas perguntas/respostas do
 * "Ver respostas" (respostasCandidato.ts). Mesmo visual dos outros PDFs do Digital (logo,
 * cor da marca e tabela do termo de equipamento — termoPdfService). Gerado no navegador.
 */

const MARGEM = 14;
const COR_MARCA: [number, number, number] = [3, 174, 252];
const COR_TEXTO: [number, number, number] = [20, 20, 20];

async function urlParaDataUrl(url: string): Promise<string> {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Falha ao carregar a logo'));
    reader.readAsDataURL(blob);
  });
}

/** Remove caracteres inválidos em nome de arquivo (Windows/macOS) — mantém acentos. */
function nomeArquivo(nome: string): string {
  return `Formulário do candidato - ${nome}`.replace(/[\\/:*?"<>|]/g, '').trim() + '.pdf';
}

export async function baixarPdfRespostasCandidato(candidato: Candidato): Promise<void> {
  const doc = new jsPDF();
  const largura = doc.internal.pageSize.getWidth();
  let y = 12;

  try {
    doc.addImage(await urlParaDataUrl(logoUrl), 'PNG', MARGEM, y, 36, 11);
  } catch {
    // Sem logo é melhor do que não gerar o PDF.
  }
  y += 18;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...COR_TEXTO);
  doc.text('Formulário do candidato', MARGEM, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(candidato.nome ?? '', MARGEM, y);
  y += 5;
  if (candidato.email) {
    doc.setTextColor(100, 100, 100);
    doc.text(candidato.email, MARGEM, y);
    y += 5;
  }
  const agora = new Date();
  doc.setFontSize(8);
  doc.setTextColor(130, 130, 130);
  doc.text(`Gerado em ${agora.toLocaleDateString('pt-BR')} às ${agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`, MARGEM, y);
  y += 3;
  doc.setDrawColor(...COR_MARCA);
  doc.setLineWidth(0.6);
  doc.line(MARGEM, y, largura - MARGEM, y);
  y += 4;

  const secoes = [
    { titulo: 'Etapa 1 — Dados pessoais', campos: montarComSeguranca(() => montarCampos(candidato)) },
    { titulo: 'Etapa 2 — Admissão', campos: montarComSeguranca(() => montarCamposEtapa2(candidato)) },
  ].filter((s) => s.campos.length > 0);

  if (secoes.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(...COR_TEXTO);
    doc.text('O candidato ainda não preencheu o formulário.', MARGEM, y + 6);
  }

  for (const secao of secoes) {
    autoTable(doc, {
      startY: y + 2,
      margin: { left: MARGEM, right: MARGEM },
      head: [[secao.titulo, '']],
      body: secao.campos.map((c) => [c.label, c.valor]),
      styles: { fontSize: 8.5, cellPadding: 2, textColor: COR_TEXTO, valign: 'top' },
      headStyles: { fillColor: COR_MARCA, textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 70, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } },
      alternateRowStyles: { fillColor: [245, 248, 252] },
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;
  }

  doc.save(nomeArquivo(candidato.nome ?? 'candidato'));
}
